import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { alpha } from '@mui/material/styles';

const getYoutubeVideoId = (url) => {
  if (!url) return null;
  const pattern = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]{11})/;
  const match = url.match(pattern);
  return match ? match[1] : null;
};

const getYoutubeEmbedUrl = (url) => {
  const id = getYoutubeVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}` : null;
};

export default function UploadVideoDialog({
  open,
  setPreviewFile,
  setSelectedFile,
  selectedFile,
  previewFile,
  isLoading,
  handleClose,
  saveVideo,
  saveYoutubeLink,
  video,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isVideoDeleted, setIsVideoDeleted] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [youtubeLinkInput, setYoutubeLinkInput] = useState('');
  const [youtubeLinkError, setYoutubeLinkError] = useState('');

  // Determine initial tab based on whether existing video is a YouTube link
  useEffect(() => {
    if (open) {
      const existingVideo = video || '';
      if (existingVideo && getYoutubeVideoId(existingVideo)) {
        setActiveTab(1);
        setYoutubeLinkInput(existingVideo);
      } else {
        setActiveTab(0);
        setYoutubeLinkInput('');
      }
      setYoutubeLinkError('');
    }
  }, [open, video]);

  useEffect(() => {
    if (!open) {
      setIsDragging(false);
      setIsVideoDeleted(false);
      setYoutubeLinkError('');
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
      }
    }
  }, [open, previewUrl]);

  useEffect(() => {
    let cleanup = null;

    if (previewFile) {
      setPreviewUrl(previewFile);
      if (previewFile) setIsVideoDeleted(false);
    } else if (selectedFile instanceof File) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      setIsVideoDeleted(false);
      cleanup = () => {
        URL.revokeObjectURL(url);
      };
    } else if (typeof selectedFile === 'string' && selectedFile) {
      setPreviewUrl(selectedFile);
      setIsVideoDeleted(false);
    } else if (video && !selectedFile && !isVideoDeleted) {
      setPreviewUrl(video);
    } else {
      setPreviewUrl('');
    }

    return cleanup || undefined;
  }, [selectedFile, video, previewFile, isVideoDeleted]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      setIsDragging(false);

      if (rejectedFiles && rejectedFiles.length > 0) {
        toast.error('Please upload a valid video file (MP4, MOV, AVI, or WebM)');
        return;
      }

      const videoFile = acceptedFiles[0];
      if (videoFile) {
        if (videoFile.size > 100 * 1024 * 1024) {
          toast.error('Video size should not exceed 100MB');
          return;
        }
        setSelectedFile(videoFile);
        setPreviewFile(URL.createObjectURL(videoFile));
        setIsVideoDeleted(false);
      }
    },
    [setSelectedFile, setPreviewFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'video/mp4': ['.mp4'],
      'video/mov': ['.mov'],
      'video/avi': ['.avi'],
      'video/webm': ['.webm'],
    },
    onDrop,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    multiple: false,
    maxSize: 100 * 1024 * 1024, // 100MB
  });

  const handleRemove = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
    setSelectedFile('');
    setPreviewFile('');
    setIsVideoDeleted(true);
  };

  const handleCancel = () => {
    handleRemove();
    setYoutubeLinkInput('');
    setYoutubeLinkError('');
    handleClose();
  };

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setYoutubeLinkError('');
  };

  const handleYoutubeLinkChange = (e) => {
    setYoutubeLinkInput(e.target.value);
    setYoutubeLinkError('');
  };

  const handleSaveYoutubeLink = () => {
    const trimmed = youtubeLinkInput.trim();
    if (!trimmed) {
      setYoutubeLinkError('Please enter a YouTube URL');
      return;
    }
    if (!getYoutubeVideoId(trimmed)) {
      setYoutubeLinkError('Please enter a valid YouTube URL (e.g. https://www.youtube.com/watch?v=...)');
      return;
    }
    saveYoutubeLink(trimmed);
  };

  const existingYoutubeEmbed = video && !isVideoDeleted ? getYoutubeEmbedUrl(video) : null;
  const previewYoutubeEmbed = youtubeLinkInput.trim() ? getYoutubeEmbedUrl(youtubeLinkInput.trim()) : null;

  // Only treat a video as a file-based video on the Upload tab if it is NOT a YouTube URL.
  // When `video` is a YouTube link, the file tab should show the empty illustration.
  const isExistingVideoYoutube = Boolean(existingYoutubeEmbed);
  const filePreviewSource = previewFile || previewUrl || (!isExistingVideoYoutube ? video : '');
  const hasFileVideo = !isVideoDeleted && Boolean(filePreviewSource);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="upload-video-dialog-title"
      aria-describedby="upload-video-dialog-description"
      scroll="paper"
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: 'calc(100dvh - 24px)',
          margin: { xs: 1.5, sm: 4 },
        },
      }}
    >
      <Box sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: { xs: 2, sm: 3 },
            pb: { xs: 1.5, sm: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>
            {hasFileVideo || existingYoutubeEmbed ? 'Manage Introduction Video' : 'Upload Introduction Video'}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 1.5, sm: 3 } }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              icon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Upload File"
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
            <Tab
              icon={<LinkIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="YouTube Link"
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 2, sm: 3 }, overflowY: 'auto', maxHeight: 'calc(100dvh - 210px)' }}>
          {/* === FILE UPLOAD TAB === */}
          {activeTab === 0 && (
            <>
              <Box
                {...getRootProps()}
                sx={{
                  position: 'relative',
                  border: '2px dashed',
                  borderColor: isDragActive || isDragging ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: { xs: 2, sm: 4 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  bgcolor: isDragActive || isDragging ? alpha('#A94B9C', 0.05) : 'background.paper',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: alpha('#A94B9C', 0.02),
                  },
                  minHeight: hasFileVideo ? 'auto' : { xs: '280px', sm: '400px' },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <input {...getInputProps()} />

                {hasFileVideo ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: 'common.black',
                        aspectRatio: '16/9',
                        maxHeight: '500px',
                      }}
                    >
                      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                      <video
                        controls
                        preload="metadata"
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        src={(() => {
                          const videoSrc = filePreviewSource;
                          if (videoSrc && typeof videoSrc === 'string' && videoSrc.includes('.s3.us-west-2.amazonaws')) {
                            return `${videoSrc}?${Date.now()}`;
                          }
                          return videoSrc;
                        })()}
                        key={filePreviewSource || 'video-preview'}
                      >
                        Your browser does not support the video tag.
                      </video>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove();
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'error.main',
                          color: 'white',
                          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.3)',
                          '&:hover': { bgcolor: 'error.dark', transform: 'scale(1.1)' },
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}>
                      Click or drag to replace video
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        bgcolor: alpha('#A94B9C', 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                      }}
                    >
                      {isDragActive || isDragging ? (
                        <CloudUploadIcon sx={{ fontSize: 50, color: 'primary.main' }} />
                      ) : (
                        <VideocamIcon sx={{ fontSize: 50, color: 'primary.main' }} />
                      )}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      {isDragActive || isDragging ? 'Drop your video here' : 'Drag & drop your video'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                      or click to browse
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Supports: MP4, MOV, AVI, WebM (Max 100MB)
                    </Typography>
                  </>
                )}
              </Box>

              {/* File upload action buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isLoading}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel
                </LoadingButton>
                <LoadingButton
                  loading={isLoading}
                  fullWidth
                  variant="contained"
                  color="main"
                  onClick={() => {
                    if (selectedFile instanceof File) {
                      saveVideo();
                    } else if (hasFileVideo && !selectedFile) {
                      handleClose();
                    } else {
                      toast.error('Please select a video to upload');
                    }
                  }}
                  disabled={isLoading}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {hasFileVideo && !selectedFile ? 'Keep Current Video' : 'Upload Video'}
                </LoadingButton>
              </Box>
            </>
          )}

          {/* === YOUTUBE LINK TAB === */}
          {activeTab === 1 && (
            <>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="YouTube URL"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={youtubeLinkInput}
                  onChange={handleYoutubeLinkChange}
                  error={Boolean(youtubeLinkError)}
                  helperText={youtubeLinkError || 'Paste a YouTube video link (e.g. https://youtu.be/abcdefghijk)'}
                  InputProps={{
                    startAdornment: <LinkIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={{ mb: 2 }}
                />

                {/* YouTube Preview */}
                {previewYoutubeEmbed ? (
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '16/9',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: '#000',
                    }}
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      src={previewYoutubeEmbed}
                      title="YouTube video preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ display: 'block' }}
                    />
                  </Box>
                ) : existingYoutubeEmbed && !youtubeLinkInput ? (
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '16/9',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: '#000',
                    }}
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      src={existingYoutubeEmbed}
                      title="Current YouTube video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ display: 'block' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '16/9',
                      borderRadius: 2,
                      bgcolor: 'background.neutral',
                      border: '2px dashed',
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <LinkIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      Video preview will appear here
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* YouTube link action buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isLoading}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel
                </LoadingButton>
                <LoadingButton
                  loading={isLoading}
                  fullWidth
                  variant="contained"
                  color="main"
                  onClick={handleSaveYoutubeLink}
                  disabled={isLoading}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Save YouTube Link
                </LoadingButton>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
